import customConfig from "../config";
import { menuConfig } from "../config/menuConfig";

/**
 * Known application roles for permission assignment UI.
 */
export const KNOWN_APP_ROLES = [
  customConfig.roleCustomerSupportL1,
  customConfig.roleCustomerSupportL2,
  customConfig.roleCustomerSupportL3,
  customConfig.roleCustomerSupportL3Special,
  customConfig.roleCustomerSupportL4,
  customConfig.roleProductManager,
  customConfig.roleCloudAdmin,
  customConfig.roleFactoryAdmin,
  customConfig.roleFieldOperator,
  customConfig.roleLineTestUser,
];

const ROLE_DISPLAY_LABELS = {
  [customConfig.roleCustomerSupportL1]: "Customer Support L1",
  [customConfig.roleCustomerSupportL2]: "Customer Support L2",
  [customConfig.roleCustomerSupportL3]: "Customer Support L3",
  [customConfig.roleCustomerSupportL3Special]: "Customer Support L3 Special",
  [customConfig.roleCustomerSupportL4]: "Customer Support L4",
  [customConfig.roleProductManager]: "Product Manager",
  [customConfig.roleCloudAdmin]: "Cloud Admin",
  [customConfig.roleFactoryAdmin]: "Factory Admin",
  [customConfig.roleFieldOperator]: "Field Operator",
  [customConfig.roleLineTestUser]: "Line Test User",
};

/** Default userLevel used when evaluating menu conditions for a single role. */
const ROLE_DEFAULT_LEVEL = {
  [customConfig.roleCustomerSupportL1]: 1,
  [customConfig.roleCustomerSupportL2]: 2,
  [customConfig.roleCustomerSupportL3]: 3,
  [customConfig.roleCustomerSupportL3Special]: 4,
  [customConfig.roleCustomerSupportL4]: 5,
  [customConfig.roleProductManager]: 3,
  [customConfig.roleCloudAdmin]: 5,
  [customConfig.roleFactoryAdmin]: 0,
  [customConfig.roleFieldOperator]: 0,
  [customConfig.roleLineTestUser]: 0,
};

export const getRoleDisplayLabel = (role) =>
  ROLE_DISPLAY_LABELS[role] || role;

export const getRoleOptions = () =>
  KNOWN_APP_ROLES.map((role) => ({
    value: role,
    label: getRoleDisplayLabel(role),
  }));

export const getDefaultLevelForRole = (role) =>
  ROLE_DEFAULT_LEVEL[role] ?? 0;

/**
 * Check whether a role currently satisfies a menu condition (from live menuConfig).
 */
export const roleCanAccessMenuItem = (condition, role) => {
  if (typeof condition !== "function") return true;
  const userLevel = getDefaultLevelForRole(role);
  try {
    return Boolean(
      condition({
        userLevel,
        roles: [role],
        tenantId: "panasonic",
      }) ||
        condition({
          userLevel,
          roles: [role],
          tenantId: "other",
        })
    );
  } catch {
    return false;
  }
};

const pageKey = (sectionId, itemId, path) =>
  [sectionId, itemId, path || ""].join("::");

const mergePageConditions = (existingCondition, nextCondition) => {
  if (typeof existingCondition !== "function") return nextCondition;
  if (typeof nextCondition !== "function") return existingCondition;
  return (context) =>
    Boolean(existingCondition(context) || nextCondition(context));
};

/** Collapse duplicate page labels (e.g. Device Details on /dashboard and /searchDevice). */
const dedupePagesByName = (sectionId, pages) => {
  const byName = new Map();

  pages.forEach((page) => {
    const existing = byName.get(page.name);
    if (!existing) {
      byName.set(page.name, {
        ...page,
        key: pageKey(sectionId, page.id, page.name),
      });
      return;
    }

    existing.condition = mergePageConditions(existing.condition, page.condition);
  });

  return Array.from(byName.values());
};

/**
 * Build a simple menu tree from live menuConfig for the permission UI.
 * Shape: [{ id, name, pages: [{ id, key, name, path, condition }] }]
 */
export const buildMenuPermissionTree = (menu = menuConfig) =>
  (menu || [])
    .map((section) => ({
      id: section.id,
      name: section.name,
      pages: dedupePagesByName(
        section.id,
        (section.subitems || []).map((item) => ({
          id: item.id,
          key: pageKey(section.id, item.id, item.path),
          name: item.name,
          path: item.path,
          condition: item.condition,
        }))
      ),
    }))
    .filter((section) => section.pages.length > 0);

/**
 * Page keys a role can access, inferred from live menuConfig conditions.
 */
export const getCheckedPagesForRole = (role, menu = menuConfig) => {
  const checked = new Set();
  buildMenuPermissionTree(menu).forEach((section) => {
    section.pages.forEach((page) => {
      if (roleCanAccessMenuItem(page.condition, role)) {
        checked.add(page.key);
      }
    });
  });
  return checked;
};
